/**
 * The model optimizer's "Adjust" section: every setting that shapes the
 * optimized output beyond the quality picker. Each group is a disclosure whose
 * summary line states the current value, so the collapsed view still answers
 * "what will it do?".
 */
import { AR_OVERSIZED_UNITS, AR_TINY_UNITS, recommendDetail, recommendErrorCeiling } from 'trace-viewer/model-utils';
import type { MaterialInfo, MaterialOverride, ModelProbeReport, OptimizationProfile, TextureInfo } from 'trace-viewer/model-utils';
import type { UiState } from './main';
import { escapeHtml, formatFileSize, renderDisclosure } from '../shared/ui';
import { detailToSlider, ERROR_MAX, ERROR_SLIDER_MAX, errorToSlider, SLIDER_MAX, SLIDER_MIN } from './geometry-slider';

/** Format an error-ceiling fraction (meshopt `error`) as the exact %. */
export function formatErrorLabel(error: number): string {
  const pct = error * 100;
  if (pct <= 0) return '0%';
  // Up to 4 decimals, trailing zeros trimmed (e.g. 0.05%, 0.012%).
  return `${pct.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

/** Format a model-unit length compactly (e.g. "0.25", "25.1"). */
function fmtUnit(v: number): string {
  if (v === 0) return '0';
  if (Math.abs(v) < 0.01) return v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  if (Math.abs(v) < 10) return v.toFixed(2);
  return v.toFixed(1);
}

function dims(report: ModelProbeReport): [number, number, number] {
  const [minX, minY, minZ, maxX, maxY, maxZ] = report.boundingBox;
  return [maxX - minX, maxY - minY, maxZ - minZ];
}

/** Whether the model's size looks wrong for AR (same thresholds as the issues list). */
export function scaleLooksOff(report: ModelProbeReport): 'large' | 'tiny' | null {
  const longest = Math.max(...dims(report));
  if (longest > AR_OVERSIZED_UNITS) return 'large';
  if (longest > 0 && longest < AR_TINY_UNITS) return 'tiny';
  return null;
}

// ─── Size & orientation ────────────────────────────────────────────────────

const SCALE_PRESETS: { factor: number; label: string }[] = [
  { factor: 1, label: 'Keep' },
  { factor: 0.01, label: 'Centimetres → metres' },
  { factor: 0.001, label: 'Millimetres → metres' },
];

function renderSizeOrientation(state: UiState, report: ModelProbeReport): string {
  const [dx, dy, dz] = dims(report);
  const longest = Math.max(dx, dy, dz);
  const off = scaleLooksOff(report);

  const sizeHelp = off
    ? `<div class="notice">At ${fmtUnit(longest)} units long, this is ${off === 'large' ? 'very large' : 'tiny'} for AR, where 1 unit is 1 metre. If it wasn’t modelled in metres, rescale it.</div>`
    : `<div class="setting-help">In AR, 1 unit is 1 metre, so this model will appear <strong>${fmtUnit(longest)} m</strong> at its longest.</div>`;

  // Explain a baked-in root transform (e.g. Unity's 100× "GameObject" wrapper):
  // the world bounds already include it, and optimize bakes it into the
  // vertices, leaving a clean unit root.
  const rt = report.rootTransform;
  // Vertex positions are float32, so a "100×" root reads back as ~99.98 — round
  // to the nearest whole factor for display when it's close.
  const tidyScale = (v: number): string => {
    const r = Math.round(v);
    return Math.abs(v - r) / Math.max(1, Math.abs(v)) < 0.01 ? String(r) : fmtUnit(v);
  };
  const rootNote = rt?.hasNonIdentityRoot
    ? `<div class="setting-help">${rt.rootUniformScale != null && Math.abs(rt.rootUniformScale - 1) > 1e-3
        ? `A parent node scales the model ${tidyScale(rt.rootUniformScale)}×; the size above includes it.`
        : 'The model sits under a transformed parent node; the size above includes it.'}
        Optimizing bakes this into the mesh.</div>`
    : '';

  const isCustom = state.scaleMode === 'custom';
  const presets = SCALE_PRESETS.map(p => `
    <label class="choice">
      <input type="radio" name="scale-preset" data-action="set-scale" data-factor="${p.factor}" ${!isCustom && state.scaleFactor === p.factor ? 'checked' : ''} />
      ${p.label}
    </label>`).join('');

  const out = state.scaleFactor === 1
    ? ''
    : `<div class="setting-numbers">Result: ${fmtUnit(dx * state.scaleFactor)} × ${fmtUnit(dy * state.scaleFactor)} × ${fmtUnit(dz * state.scaleFactor)} m</div>`;

  const body = `
    <div class="settings">
      <div class="setting">
        <div class="setting-head"><span class="setting-name">Size</span><span class="setting-value">${fmtUnit(dx)} × ${fmtUnit(dy)} × ${fmtUnit(dz)}</span></div>
        ${sizeHelp}
        ${rootNote}
      </div>
      <div class="setting">
        <span class="setting-name">Rescale</span>
        <div class="choice-row">
          ${presets}
          <label class="choice">
            <input type="radio" name="scale-preset" data-action="set-scale-custom" ${isCustom ? 'checked' : ''} />
            Custom ×
            <input type="number" data-setting="scaleCustom" step="any" min="0" value="${isCustom ? state.scaleFactor : ''}" placeholder="1.0" aria-label="Custom scale factor" />
          </label>
        </div>
        ${out}
      </div>
      <div class="setting">
        <label class="toggle" style="color:var(--text);">
          <input type="checkbox" data-setting="axisConversion" ${state.axisConversion === 'zUpToYUp' ? 'checked' : ''} />
          Stand it upright (Z-up → Y-up)
        </label>
        <div class="setting-help">Turn on if the model is lying on its back — common for files from Blender or CAD tools.</div>
      </div>
    </div>
  `;

  const meta = [
    `${fmtUnit(dx)} × ${fmtUnit(dy)} × ${fmtUnit(dz)}${state.scaleFactor !== 1 ? ` ×${state.scaleFactor}` : ''}`,
    state.axisConversion === 'zUpToYUp' ? 'upright' : '',
  ].filter(Boolean).join(', ');
  return renderDisclosure('size', 'Size and orientation', body, { meta, metaTone: off ? 'warn' : undefined, open: off != null });
}

// ─── Textures ──────────────────────────────────────────────────────────────

const TEXTURE_RESOLUTION_OPTIONS = [512, 1024, 2048] as const;

function renderTextureSettings(profile: OptimizationProfile, report: ModelProbeReport): string {
  if (report.textures.textureCount === 0) return '';
  const active = profile.maxTextureSideLength;
  const choices = TEXTURE_RESOLUTION_OPTIONS.map(px => `
    <label class="choice">
      <input type="radio" name="texture-res" value="${px}" ${px === active ? 'checked' : ''} />
      ${px} px
    </label>`).join('');
  const custom = TEXTURE_RESOLUTION_OPTIONS.includes(active as 512 | 1024 | 2048)
    ? ''
    : `<div class="setting-help">Currently ${active} px.</div>`;

  const body = `
    <div class="settings">
      <div class="setting">
        <span class="setting-name">Largest texture size</span>
        <div class="choice-row">${choices}</div>
        ${custom}
        <div class="setting-help">Bigger textures are scaled down to this. Smaller ones are left alone.</div>
      </div>
      <div class="setting">
        <div class="setting-head"><span class="setting-name">JPEG quality</span><span class="setting-value" data-value-for="jpgQuality">${profile.jpgQuality}%</span></div>
        <input type="range" data-setting="jpgQuality" min="40" max="100" step="1" value="${profile.jpgQuality}" aria-label="JPEG quality" />
        <div class="setting-help">Used for color textures only. Normal and roughness maps, and textures that are already JPEG, keep their quality.</div>
      </div>
    </div>
  `;
  return renderDisclosure('textures', 'Textures', body, { meta: `Up to ${active} px, JPEG ${profile.jpgQuality}%` });
}

// ─── Mesh detail ───────────────────────────────────────────────────────────

/** Plain-language descriptor for a retention ratio (1 = unchanged). */
function describeDetail(ratio: number): string {
  if (ratio >= 1) return 'The mesh is left exactly as modelled.';
  if (ratio >= 0.9) return 'Barely touches the mesh.';
  if (ratio >= 0.7) return 'Safe for most models.';
  if (ratio >= 0.4) return 'Good for very dense meshes.';
  return 'Expect visible simplification.';
}

/** meshoptimizer's "mesh radius" ≈ half the bounding-box diagonal, in model units. */
function meshRadius(report: ModelProbeReport): number {
  const [dx, dy, dz] = dims(report);
  return 0.5 * Math.hypot(dx, dy, dz);
}

/** Absolute deviation for a given error fraction, e.g. "about 1.1 mm". */
function fmtAbsoluteError(error: number, report: ModelProbeReport): string {
  const dist = error * meshRadius(report); // model units
  if (dist <= 0) return '';
  // glTF is conventionally metres; show mm when small, else cm/units.
  if (dist < 0.01) return `about ${(dist * 1000).toFixed(1)} mm`;
  if (dist < 1) return `about ${(dist * 100).toFixed(1)} cm`;
  return `about ${dist.toFixed(2)} units`;
}

function renderMeshDetail(state: UiState, report: ModelProbeReport): string {
  const profile = state.selectedProfile;
  const ratio = profile.geometryDetail ?? 0.9;
  const sliderVal = detailToSlider(ratio);
  const rec = recommendDetail(report);
  const recPos = detailToSlider(rec.detail);
  const why = rec.overTessellation >= 4
    ? `It’s about ${rec.overTessellation.toFixed(0)}× denser than a typical AR mesh.`
    : rec.overTessellation >= 1.5
      ? 'It’s moderately dense.'
      : 'It’s already fairly light.';
  const recLine = `
    <div class="setting-help">
      Suggested for this model: <strong>${recPos}%</strong>. ${why}
      ${recPos !== sliderVal ? '<button type="button" class="btn-link" data-action="use-recommended-detail">Use suggestion</button>' : ''}
    </div>`;

  // Actual achieved counts once an optimized result exists; otherwise the target.
  const t = Math.round(report.geometry.triangleCount);
  const optimized = state.optimization.report;
  let numbers: string;
  if (optimized) {
    const ot = Math.round(optimized.geometry.triangleCount);
    numbers = `${t.toLocaleString()} → ${ot.toLocaleString()} triangles in the current result`;
  } else if (ratio >= 1) {
    numbers = `${t.toLocaleString()} triangles, unchanged`;
  } else {
    numbers = `${t.toLocaleString()} → about ${Math.round(t * ratio).toLocaleString()} triangles`;
  }

  // Advanced: the simplification error ceiling (meshoptimizer's `error`) — the
  // max distance the surface may move, as a fraction of the mesh radius.
  const autoVal = recommendErrorCeiling(report);
  const usingAuto = profile.errorCeiling === undefined;
  const effective = Math.min(profile.errorCeiling ?? autoVal, ERROR_MAX);
  const abs = fmtAbsoluteError(effective, report);
  const errorBody = `
    <div class="setting">
      <div class="setting-head">
        <span class="setting-name">Maximum surface shift</span>
        <span class="setting-value" data-value-for="errorCeiling">${formatErrorLabel(effective)}</span>
      </div>
      <input type="range" data-setting="errorCeiling" min="0" max="${ERROR_SLIDER_MAX}" step="1" value="${errorToSlider(effective)}" aria-label="Maximum surface shift" />
      <div class="setting-scale"><span>0% (only remove redundant points)</span><span>${formatErrorLabel(ERROR_MAX)} of model size</span></div>
      <div class="setting-help">
        How far the simplified surface may move from the original${abs ? ` (${abs} on this model)` : ''}. Lower protects smooth, curved surfaces from dents.
        ${usingAuto ? 'Set automatically per mesh.' : `<button type="button" class="btn-link" data-action="reset-error-ceiling">Set automatically (${formatErrorLabel(autoVal)})</button>`}
      </div>
    </div>`;

  const body = `
    <div class="settings">
      <div class="setting">
        <div class="setting-head">
          <span class="setting-name">Detail to keep</span>
          <span class="setting-value" data-value-for="geometryDetail">${ratio >= 1 ? '100% (unchanged)' : `${sliderVal}%`}</span>
        </div>
        <input type="range" data-setting="geometryDetail" min="${SLIDER_MIN}" max="${SLIDER_MAX}" step="1" value="${sliderVal}" aria-label="Detail to keep" />
        <div class="setting-help">${describeDetail(ratio)}</div>
        <div class="setting-numbers">${numbers}</div>
        ${recLine}
      </div>
      ${renderDisclosure('error-ceiling', 'Advanced', errorBody)}
    </div>
  `;
  return renderDisclosure('mesh', 'Mesh detail', body, { meta: ratio >= 1 ? 'Unchanged' : `Keep ${sliderVal}%` });
}

// ─── Materials ─────────────────────────────────────────────────────────────

const SLOT_LABELS: Record<string, string> = {
  baseColor: 'Base color',
  normal: 'Normal',
  metallicRoughness: 'Metal / rough',
  occlusion: 'Occlusion',
  emissive: 'Emissive',
};

const ALPHA_USAGE_LABEL: Record<string, string> = {
  none: 'no transparency',
  opaque: 'transparency unused',
  binary: 'cut-out edges',
  smooth: 'soft transparency',
};

function renderMaterialMap(slot: string, tex: TextureInfo | undefined): string {
  const fmt = tex?.mimeType === 'image/jpeg' ? 'JPEG' : tex?.mimeType === 'image/png' ? 'PNG' : '';
  const size = tex?.width && tex?.height ? `${tex.width}×${tex.height}` : '';
  const alpha = tex?.alpha?.usage ? ALPHA_USAGE_LABEL[tex.alpha.usage] : '';
  const meta = [fmt, size, tex ? formatFileSize(tex.sizeBytes) : '', alpha].filter(Boolean).join(', ');
  const thumb = tex?.dataUrl ? `<img src="${escapeHtml(tex.dataUrl)}" alt="" />` : '<span class="map-empty"></span>';
  return `
    <div class="material-map">
      ${thumb}
      <div>
        <div class="material-map-slot">${escapeHtml(SLOT_LABELS[slot] ?? slot)}</div>
        <div class="material-map-meta">${escapeHtml(meta)}</div>
      </div>
    </div>`;
}

function renderSelect(
  matName: string,
  field: string,
  label: string,
  options: Array<{ value: string; text: string }>,
  selected: string,
  original: string,
  recommended: string | undefined,
  changed: boolean,
): string {
  const opts = options.map(o => {
    const tags = [o.value === original ? 'original' : '', recommended !== undefined && o.value === recommended ? 'suggested' : '']
      .filter(Boolean).join(', ');
    return `<option value="${o.value}" ${o.value === selected ? 'selected' : ''}>${escapeHtml(o.text)}${tags ? ` (${tags})` : ''}</option>`;
  }).join('');
  return `
    <label class="material-control">
      <span>${escapeHtml(label)}${changed ? ' <span class="changed-tag">changed</span>' : ''}</span>
      <select class="field-select${changed ? ' changed' : ''}" data-material="${escapeHtml(matName)}" data-field="${field}">${opts}</select>
    </label>`;
}

function renderMaterialControls(mat: MaterialInfo, overrides: Record<string, MaterialOverride>): string {
  const ov = overrides[mat.name];
  const alpha = renderSelect(
    mat.name, 'alphaMode', 'Transparency',
    [{ value: 'OPAQUE', text: 'Opaque' }, { value: 'MASK', text: 'Cut-out (mask)' }, { value: 'BLEND', text: 'Blended' }],
    ov?.alphaMode ?? mat.alphaMode, mat.alphaMode, mat.recommended.alphaMode, ov?.alphaMode !== undefined,
  );
  const fmtEff = ov?.baseColorFormat === 'jpeg' || ov?.baseColorFormat === 'png' ? ov.baseColorFormat : mat.baseColorFormat;
  const fmt = fmtEff !== undefined
    ? renderSelect(
        mat.name, 'baseColorFormat', 'Color texture format',
        [{ value: 'png', text: 'PNG' }, { value: 'jpeg', text: 'JPEG' }],
        fmtEff, mat.baseColorFormat ?? 'png', mat.recommended.baseColorFormat, ov?.baseColorFormat !== undefined,
      )
    : '';
  const dsChanged = ov?.doubleSided !== undefined;
  const ds = `
    <div class="material-control">
      <span>Faces${dsChanged ? ' <span class="changed-tag">changed</span>' : ''}</span>
      <label class="toggle" style="color:var(--text);padding-top:0.3rem;">
        <input type="checkbox" data-material="${escapeHtml(mat.name)}" data-field="doubleSided" ${(ov?.doubleSided ?? mat.doubleSided) ? 'checked' : ''} />
        Double-sided${mat.recommended.doubleSided === false ? ' <span class="muted">(suggested: off)</span>' : ''}
      </label>
    </div>`;
  return `<div class="material-controls">${alpha}${fmt}${ds}</div>`;
}

function renderMaterial(mat: MaterialInfo, allTextures: TextureInfo[], overrides: Record<string, MaterialOverride>): string {
  const facts = [
    mat.alphaMode === 'OPAQUE' ? 'Opaque' : mat.alphaMode === 'MASK' ? `Cut-out at ${mat.alphaCutoff.toFixed(2)}` : 'Blended',
    mat.doubleSided ? 'double-sided' : '',
    `metal ${mat.metallicFactor.toFixed(1)}, rough ${mat.roughnessFactor.toFixed(1)}`,
  ].filter(Boolean).join(', ');
  const maps = mat.textures.length
    ? mat.textures.map(ref => renderMaterialMap(ref.slot, allTextures[ref.textureIndex])).join('')
    : '<div class="material-map-meta">No textures, color only</div>';
  const hints = mat.hints.length
    ? `<ul class="material-hints">${mat.hints.map(h => `
        <li class="material-hint${h.severity === 'warn' ? ' warn' : ''}">${escapeHtml(h.title)}<span>${escapeHtml(h.detail)}</span></li>`).join('')}</ul>`
    : '';
  return `
    <div class="material">
      <div class="material-head">
        <h3 class="material-name">${escapeHtml(mat.name)}</h3>
        <span class="material-facts">${escapeHtml(facts)}</span>
      </div>
      <div class="material-maps">${maps}</div>
      ${hints}
      ${renderMaterialControls(mat, overrides)}
    </div>`;
}

function renderMaterials(report: ModelProbeReport, overrides: Record<string, MaterialOverride>): string {
  const mats = report.materials.materials ?? [];
  if (mats.length === 0) return '';
  const allTextures = report.textures.textures ?? [];
  const warnCount = mats.reduce((n, m) => n + m.hints.filter(h => h.severity === 'warn').length, 0);
  const edited = Object.keys(overrides).length;
  const meta = edited > 0
    ? `${edited} ${edited === 1 ? 'fix' : 'fixes'} applied`
    : warnCount > 0
      ? `${warnCount} to review`
      : `${mats.length} ${mats.length === 1 ? 'material' : 'materials'}, no issues`;
  const body = `
    ${edited > 0 ? '<p class="setting-help" style="margin-top:0;">Suggested fixes are already selected. Change any of them below.</p>' : ''}
    <div class="material-list">${mats.map(m => renderMaterial(m, allTextures, overrides)).join('')}</div>
  `;
  return renderDisclosure('materials', 'Materials', body, {
    meta,
    metaTone: edited > 0 ? 'accent' : warnCount > 0 ? 'warn' : undefined,
    open: edited === 0 && warnCount > 0,
  });
}

// ─── Section ───────────────────────────────────────────────────────────────

export function renderAdjust(state: UiState, dirty: boolean): string {
  const report = state.report;
  if (!report || report.bailout) return '';
  const apply = dirty && !state.optimization.running
    ? `<div class="status-actions" style="margin-top:0.875rem;">
         <button class="btn-primary" data-action="optimize">Apply changes</button>
         <span class="setting-help">Updates the preview and download.</span>
       </div>`
    : '';
  return `
    <section class="section">
      <h2 class="section-title">Adjust</h2>
      ${renderSizeOrientation(state, report)}
      ${renderTextureSettings(state.selectedProfile, report)}
      ${renderMeshDetail(state, report)}
      ${renderMaterials(report, state.materialOverrides)}
      ${apply}
    </section>
  `;
}
